import { useState, useEffect } from 'react';

// ==============================|| CONFIG - LOCAL STORAGE ||============================== //

export default function useLocalStorage(key, defaultValue) {
    const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    const [value, setValue] = useState(() => {
        if (!isBrowser) return defaultValue;
        try {
            const storedValue = localStorage.getItem(key);
            return storedValue === null ? defaultValue : JSON.parse(storedValue);
        } catch (e) {
            // Fallback to default on SSR or JSON parse errors
            return defaultValue;
        }
    });

    useEffect(() => {
        if (!isBrowser) return undefined;
        const listener = (e) => {
            if (e.storageArea === localStorage && e.key === key) {
                try {
                    setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
                } catch (err) {
                    // If parsing fails, reset to default
                    setValue(defaultValue);
                }
            }
        };
        window.addEventListener('storage', listener);

        return () => {
            window.removeEventListener('storage', listener);
        };
    }, [key, defaultValue, isBrowser]);

    const setValueInLocalStorage = (newValue) => {
        setValue((currentValue) => {
            const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;
            if (isBrowser) {
                try {
                    localStorage.setItem(key, JSON.stringify(result));
                } catch (err) {
                    // Swallow quota/security errors in non-browser contexts
                    // eslint-disable-next-line no-console
                    console.warn('useLocalStorage: failed to persist value', err);
                }
            }
            return result;
        });
    };

    return [value, setValueInLocalStorage];
}
